import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import PublicMapComponent from './PublicMapComponent';
import TopSearchBar from './TopSearchBar';
import { permanentLayers } from '../../data/layersConfig';
import { permanentLayersGeojson } from '../../data/permanentLayersGeojson';
import { buildFeatureIndex } from '../../utils/featureIndex';
import { FeatureIndexItem } from '../FeatureSearch';

const PublicMapPage: React.FC = () => {
    const [permanentGeojsons, setPermanentGeojsons] = useState<Array<{ id: string; name: string; geojson: GeoJSON.FeatureCollection }>>([]);
    const [flyToTarget, setFlyToTarget] = useState<{ lat: number; lng: number } | null>(null);
    const [flyToTrigger, setFlyToTrigger] = useState(false);
    const [flyToZoom, setFlyToZoom] = useState<number | null>(null);

    // Fetch permanent GeoJSONs on mount
    useEffect(() => {
        let mounted = true;

        const fetchLayers = async () => {
            const results = [];

            for (const layer of permanentLayersGeojson) {
                if (layer.type === 'group' && layer.children) {
                    for (const childLayer of layer.children) {
                        try {
                            if (!childLayer.url) continue;
                            const resp = await fetch(childLayer.url);
                            const geojson = await resp.json();
                            results.push({ id: childLayer.id, name: childLayer.name, geojson });
                        } catch (error) {
                            console.error(`Failed to load child layer ${childLayer.name}:`, error);
                        }
                    }
                } else {
                    try {
                        if (!layer.url) continue;
                        const resp = await fetch(layer.url);
                        const geojson = await resp.json();
                        results.push({ id: layer.id, name: layer.name, geojson });
                    } catch (error) {
                        console.error(`Failed to load layer ${layer.name}:`, error);
                    }
                }
            }

            if (mounted) {
                setPermanentGeojsons(results);
            }
        };

        fetchLayers();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        const originalContent = viewport?.getAttribute('content');

        if (viewport) {
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }

        return () => {
            if (viewport && originalContent) {
                viewport.setAttribute('content', originalContent);
            }
        };
    }, []);

    const featureIndex: FeatureIndexItem[] = buildFeatureIndex({
        permanentGeojsons,
        importedLayers: []
    });

    const handleFeatureSelect = (feature: FeatureIndexItem) => {
        if (!feature || !feature.geometry) return;

        if (feature.geometry.type === 'Point') {
            const [lng, lat] = feature.geometry.coordinates;
            setFlyToTarget({ lat, lng });
            setFlyToZoom(18);
            setFlyToTrigger(prev => !prev);
        } else if (feature.bounds) {
            const [[minLng, minLat], [maxLng, maxLat]] = feature.bounds;
            const centerLng = (minLng + maxLng) / 2;
            const centerLat = (minLat + maxLat) / 2;
            setFlyToTarget({ lat: centerLat, lng: centerLng });
            setFlyToZoom(16);
            setFlyToTrigger(prev => !prev);
        }
    };

    return (
        <Box sx={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
            <TopSearchBar
                featureIndex={featureIndex}
                onFeatureSelect={handleFeatureSelect}
            />
            <PublicMapComponent
                layers={permanentLayers}
                center={[-10.95, 21.0]}
                zoom={6}
                flyToTarget={flyToTarget}
                flyToTrigger={flyToTrigger}
                flyToZoom={flyToZoom}
            />
        </Box>
    );
};

export default PublicMapPage;
